/**
 * 경주 스탬프 랠리 — 위치기반 인증·부정방지 (FR-RALLY, docs/build/07).
 * 클라(표시)·엣지(검증) 공유. 위치 데이터 최소화: 인증 순간 단일 좌표만 검증 후 폐기(FR-RALLY-08).
 */
import { configuredUrl } from "./placeholder";
import type {
  RallyPoint,
  RallyVerify,
  RallyClaimReason,
} from "./index";

export interface RallyHours {
  open: string; // "09:00"
  close: string; // "21:00"
}
export interface RallyEvent {
  start?: string; // ISO8601
  end?: string;
  hours?: RallyHours;
  winners: number; // 선착순 상품 인원
  claimFormUrl?: string;
}
export interface RallyConfig {
  event: RallyEvent;
  points: RallyPoint[];
}

export const MAX_ACCURACY_M = 50; // 이보다 부정확하면 인증 불가(FR-RALLY-02)
export const MIN_CLAIM_INTERVAL_S = 30; // 연속 인증 최소 간격(부정 방지)
export const MAX_TRAVEL_SPEED_MPS = 30; // ~108km/h. 초과 이동은 비현실(too_fast)
const KST_OFFSET_MIN = 540; // +09:00

/** 두 좌표 간 거리(m) — Haversine. */
export function haversine(
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number,
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

export interface Coord {
  lat: number;
  lng: number;
  accuracy: number;
}

/** 지오펜스: 정확도 + 반경 내 여부. FR-RALLY-02. */
export function checkGeofence(
  point: Pick<RallyPoint, "lat" | "lng" | "radius_m">,
  coord: Coord,
): { ok: boolean; reason?: RallyClaimReason; distance: number } {
  // 좌표 유효성 먼저 검증 — NaN·범위 밖이면 haversine 이 NaN 을 내고 `NaN > radius` 가
  // false 가 되어 지오펜스가 우회됨(서버 권위 위치검증 무력화 방지, 불변식 #7).
  if (
    !Number.isFinite(coord.lat) ||
    !Number.isFinite(coord.lng) ||
    Math.abs(coord.lat) > 90 ||
    Math.abs(coord.lng) > 180
  ) {
    return { ok: false, reason: "low_accuracy", distance: NaN };
  }
  const distance = haversine(coord.lat, coord.lng, point.lat, point.lng);
  if (!Number.isFinite(coord.accuracy) || coord.accuracy > MAX_ACCURACY_M) {
    return { ok: false, reason: "low_accuracy", distance };
  }
  if (distance > point.radius_m) {
    return { ok: false, reason: "out_of_range", distance };
  }
  return { ok: true, distance };
}

/** KST 기준 운영시간(HH:MM) 내 여부. FR-RALLY-09. */
export function withinHours(hours: RallyHours | undefined, nowMs: number): boolean {
  if (!hours) return true;
  const mins = Math.floor(nowMs / 60000 + KST_OFFSET_MIN) % 1440;
  const o = toMinutes(hours.open);
  const c = toMinutes(hours.close);
  if (o === null || c === null) return true;
  // 자정 넘는 창(open>close, 예 22:00~02:00) 처리 — 아니면 해당 시간 내내 claim 불가.
  return o <= c ? mins >= o && mins <= c : mins >= o || mins <= c;
}

/** 이벤트 기간 내 여부. */
export function withinEvent(event: RallyEvent, nowMs: number): boolean {
  const s = parse(event.start);
  const e = parse(event.end);
  if (s !== null && nowMs < s) return false;
  if (e !== null && nowMs > e) return false;
  return true;
}

/**
 * 직전 인증 대비 이동이 물리적으로 불가능한지(스푸핑·연타 방지). FR-RALLY-07.
 * 절대 최소 간격 미만이거나, 거리/시간이 최대 속도를 초과하면 too_fast.
 */
export function isTooFast(
  prevTs: number | null,
  nowMs: number,
  distanceM: number,
): boolean {
  if (prevTs === null) return false;
  const dtS = (nowMs - prevTs) / 1000;
  if (dtS < MIN_CLAIM_INTERVAL_S) return true;
  if (distanceM > 0 && distanceM / dtS > MAX_TRAVEL_SPEED_MPS) return true;
  return false;
}

export interface ClaimContext {
  nowMs: number;
  prevTs: number | null; // 직전 스탬프 시각(서버)
  prevDistanceM: number; // 직전 지점과의 거리(too_fast용)
  expectedQrToken?: string; // onsite_qr 지점의 서버 토큰
}

/** 한 클레임 요청을 검증 → 인정 여부 + 거부 사유. 서버 권위(FR-RALLY-04/07/08/09). */
export function validateClaim(
  event: RallyEvent,
  point: Pick<RallyPoint, "lat" | "lng" | "radius_m" | "verify">,
  req: { method: RallyVerify; coord?: Coord; qrToken?: string | null },
  ctx: ClaimContext,
): { ok: boolean; reason?: RallyClaimReason } {
  if (!withinEvent(event, ctx.nowMs)) return { ok: false, reason: "event_ended" };
  if (!withinHours(event.hours, ctx.nowMs)) return { ok: false, reason: "closed_hours" };

  // onsite_qr: QR 토큰 일치 AND 지오펜스 동시(FR-RALLY-03)
  if (point.verify === "onsite_qr") {
    if (!req.qrToken || req.qrToken !== ctx.expectedQrToken) {
      return { ok: false, reason: "out_of_range" };
    }
  }
  // 모든 방식이 지오펜스를 요구(AR만으로 위치 증명 불가, 07 §4)
  if (!req.coord) return { ok: false, reason: "low_accuracy" };
  const geo = checkGeofence(point, req.coord);
  if (!geo.ok) return { ok: false, reason: geo.reason };

  if (isTooFast(ctx.prevTs, ctx.nowMs, ctx.prevDistanceM)) {
    return { ok: false, reason: "too_fast" };
  }
  return { ok: true };
}

export interface RallyProgress {
  collected: number;
  total: number;
  completed: boolean;
}
export function rallyProgress(
  points: RallyPoint[],
  claimedIds: readonly string[],
): RallyProgress {
  const set = new Set(claimedIds);
  const collected = points.filter((p) => set.has(p.id)).length;
  return { collected, total: points.length, completed: collected >= points.length && points.length > 0 };
}

// ── 정규화(content/rally.yaml → JSON) ──────────────────────
export function normalizeRally(input: unknown): RallyConfig {
  const o = (input ?? {}) as Record<string, unknown>;
  const ev = (o.event ?? {}) as Record<string, unknown>;
  const hoursRaw = ev.hours as Record<string, unknown> | undefined;
  const event: RallyEvent = {
    start: str(ev.start),
    end: str(ev.end),
    hours:
      hoursRaw && str(hoursRaw.open) && str(hoursRaw.close)
        ? { open: String(hoursRaw.open), close: String(hoursRaw.close) }
        : undefined,
    winners: num(ev.winners, 50),
    // 아직 안 채운 주소(`https://naver.me/REPLACE`)면 없는 것으로 —
    // 화면이 `claimFormUrl` 이 있을 때만 「선물 신청」을 그리므로, 걸러 두면 버튼 자체가 안 나온다.
    // 안 거르면 완주한 아이가 **없는 폼**으로 간다(events·AR·체험공간은 이미 이렇게 하고 있었다).
    claimFormUrl: configuredUrl(str(ev.claim_form_url) ?? str(ev.claimFormUrl)),
  };
  const rawPoints = Array.isArray(o.points) ? o.points : [];
  const points: RallyPoint[] = rawPoints
    .map((p): RallyPoint | null => {
      if (!p || typeof p !== "object") return null;
      const pt = p as Record<string, unknown>;
      const id = str(pt.id);
      const title = str(pt.title_ko);
      if (!id || !title || typeof pt.lat !== "number" || typeof pt.lng !== "number") {
        return null;
      }
      const verify: RallyVerify = pt.verify === "onsite_qr" ? "onsite_qr" : "geofence_ar";
      return {
        id,
        title_ko: title,
        lat: pt.lat,
        lng: pt.lng,
        radius_m: num(pt.radius_m, 80),
        verify,
        ar_asset: str(pt.ar_asset),
      };
    })
    .filter((p): p is RallyPoint => p !== null);
  return { event, points };
}

function toMinutes(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}
function parse(iso?: string): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}
function str(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}
function num(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

/**
 * 🎯 **서버가 실제로 받아들이는 지점** — 관리자와 엣지가 같은 잣대를 쓴다.
 *
 * ## 왜 필요한가
 * 지점 검사가 **두 곳에 따로** 있었다. 관리자는 화면에 21개를 보여 주는데 엣지는 20개만
 * 저장하고, 반지름 900m 를 적어도 서버는 500m 로 줄인다. 그런데 **아무 말도 안 했다** —
 * 운영자는 저장했다고 믿고, 아이 화면에서만 다르게 동작한다.
 *
 * 랠리는 **경주 현장에 종이 QR 을 붙여야** 돌아가는 기능이다. 지점 하나가 조용히 빠지면
 * 그 자리에 붙인 QR 이 영영 안 먹고, 현장에 다시 가 보기 전에는 알 수가 없다.
 *
 * ## 규칙(서버 기준 — 여기가 authoritative)
 * 잣대는 **엣지의 것**을 쓴다. 서버는 밖에서 오는 값을 못 믿기 때문이다(불변식 7).
 * 관리자는 이걸 **저장 전에** 불러서 「무엇이 안 들어가는지」 미리 보여 준다.
 */
export const RALLY_POINTS_MAX = 20;
export const RALLY_RADIUS_MIN = 10;
export const RALLY_RADIUS_MAX = 500;
export const RALLY_TITLE_MAX = 40;

/** 왜 못 받았나 — 관리자가 **그대로 보여줄** 말. */
export type RallyDropReason =
  | "bad_id"
  | "no_title"
  | "duplicate"
  | "bad_coord"
  | "too_many";

export const RALLY_DROP_SAY: Record<RallyDropReason, string> = {
  bad_id: "이름표는 영어 소문자·숫자·밑줄만 돼요",
  no_title: "지점 이름을 적어 주세요",
  duplicate: "이름표가 겹쳐요",
  bad_coord: "위도·경도가 지도 밖이에요",
  too_many: `지점은 ${RALLY_POINTS_MAX}개까지예요`,
};

export interface RallySanitizeResult {
  points: RallyPoint[];
  /** 못 받은 지점 — 관리자가 줄마다 보여 준다. */
  dropped: { id: string; reason: RallyDropReason }[];
  /** 값이 **바뀐** 것(버리진 않았다). 「저장했는데 다르다」를 막는다. */
  changed: { id: string; what: string }[];
}

const ID_RE = /^[a-z0-9_]{1,40}$/;

/** 서버가 받아들이는 형태로 다듬고, **무엇을 버리고 무엇을 바꿨는지** 같이 돌려준다. */
export function sanitizeRallyPoints(raw: unknown): RallySanitizeResult {
  const out: RallyPoint[] = [];
  const dropped: RallySanitizeResult["dropped"] = [];
  const changed: RallySanitizeResult["changed"] = [];
  if (!Array.isArray(raw)) return { points: out, dropped, changed };

  const seen = new Set<string>();
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const rawId = typeof o.id === "string" ? o.id : "";
    const label = rawId || "(이름표 없음)";

    if (out.length >= RALLY_POINTS_MAX) {
      dropped.push({ id: label, reason: "too_many" });
      continue;
    }
    if (!ID_RE.test(rawId)) {
      dropped.push({ id: label, reason: "bad_id" });
      continue;
    }
    if (seen.has(rawId)) {
      dropped.push({ id: label, reason: "duplicate" });
      continue;
    }
    const rawTitle = typeof o.title_ko === "string" ? o.title_ko.trim() : "";
    if (!rawTitle) {
      dropped.push({ id: label, reason: "no_title" });
      continue;
    }
    const lat = typeof o.lat === "number" ? o.lat : NaN;
    const lng = typeof o.lng === "number" ? o.lng : NaN;
    if (!Number.isFinite(lat) || lat < -90 || lat > 90 || !Number.isFinite(lng) || lng < -180 || lng > 180) {
      dropped.push({ id: label, reason: "bad_coord" });
      continue;
    }

    const title = rawTitle.slice(0, RALLY_TITLE_MAX);
    if (title !== rawTitle) changed.push({ id: rawId, what: `이름이 ${RALLY_TITLE_MAX}자로 줄어요` });

    const rawR = typeof o.radius_m === "number" ? Math.round(o.radius_m) : 80;
    const radius = Math.max(RALLY_RADIUS_MIN, Math.min(RALLY_RADIUS_MAX, rawR));
    if (radius !== rawR) changed.push({ id: rawId, what: `반경이 ${radius}m 로 바뀌어요` });

    seen.add(rawId);
    out.push({
      id: rawId,
      title_ko: title,
      lat,
      lng,
      radius_m: radius,
      verify: o.verify === "onsite_qr" ? "onsite_qr" : "geofence_ar",
      ar_asset: typeof o.ar_asset === "string" && ID_RE.test(o.ar_asset) ? o.ar_asset : undefined,
    });
  }
  return { points: out, dropped, changed };
}

/**
 * 🗣 **눌렀는데 안 됐을 때 아이에게 할 말.**
 *
 * 🔴 예전에는 이 표가 **두 곳에 따로** 있었다(`Rally.tsx` · `RallyActions.tsx`).
 * 같은 아이가 같은 순간에 겪는 일인데 문구가 달랐고(「가까이 가서 다시 시도해 주세요」 ↔
 * 「가까이 가서 눌러 주세요」), 이유를 하나 늘리자 **한 곳만 컴파일이 깨졌다** —
 * 그때 알았다. 화면이 셋으로 늘면 다음에는 조용히 갈린다.
 *
 * 문구 규칙:
 * - **아이 잘못이 아닌 것은 아이 잘못처럼 말하지 않는다**(`busy`)
 * - **다시 누르면 되는지**를 반드시 말한다 — 아이는 지금 그 자리에 서 있다
 */
export const RALLY_REASON_SAY: Record<RallyClaimReason, string> = {
  out_of_range: "아직 이 장소 근처가 아니에요. 가까이 가서 눌러 주세요.",
  low_accuracy: "위치가 정확하지 않아요. 야외에서 잠시 후 다시 눌러 주세요.",
  closed_hours: "지금은 랠리 운영시간이 아니에요.",
  event_ended: "랠리 기간이 아니에요.",
  already: "이미 도장을 받은 곳이에요!",
  too_fast: "너무 빨라요! 잠시 후 다시 눌러 주세요.",
  // 🔴 **그 아이 잘못이 아니다.** 같은 와이파이를 쓰는 친구가 많은 것뿐이라,
  //    「너무 빨라요」라고 하면 자기가 뭘 잘못한 줄 안다.
  busy: "지금 같이 하는 친구가 많아요. 조금 뒤에 다시 눌러 주세요.",
  // 🔴 「찍혔다」고 하면 안 된다 — 아이는 지금 그 자리에 서 있다. 다시 누르면 된다
  save_failed: "도장을 저장하지 못했어요. 그 자리에서 한 번 더 눌러 주세요.",
};

/** 이유를 모를 때까지 포함해 한 문장으로. */
export function rallyReasonSay(reason: RallyClaimReason | undefined): string {
  return (reason && RALLY_REASON_SAY[reason]) || "인증에 실패했어요. 잠시 후 다시 눌러 주세요.";
}

/**
 * 📷 **현장 QR 이 없는 지점을 가리킬 때.**
 *
 * 종이는 되돌릴 수 없다. 운영자가 지점 이름을 바꾸거나 지우면 **이미 가게에 붙어 있는 QR**
 * 은 없는 곳을 가리킨다. 예전엔 그때 화면이 **아무 말도 안 했다**(실측: 10초 동안 침묵) —
 * 아이는 카메라만 껐다 켜고, 보호자는 폰이 고장난 줄 안다.
 *
 * 「지점이 없다」고 하면 아이는 못 알아듣는다. **다음에 무엇을 하면 되는지**로 말한다.
 */
export function rallyQrUnknownSay(): string {
  return "이 QR 이 가리키는 곳을 못 찾았어요. 아래 목록에서 직접 골라 눌러 주세요.";
}
